/**
 * The Organization tab — the reference `ToolsList`'s shared tab (item BG).
 *
 * Scoped to the tree's selected vendor and year, exactly as My Skills is
 * (owner, 2026-08-25): the organization's skills carry the same target axis,
 * and Download lands the copy in *that* year's store. Cards, not rows — the
 * same card the local list uses, plus the publisher and the cloud version.
 * Each card offers exactly one of: **Download**, **Saved** (a local copy
 * already tracks this cloud id — in any year), or the spinner while syncing;
 * the owner also gets the cloud delete.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { atVersion, cloudSkillTarget, cloudSkillsForVendor, describeTarget, sortSkills, filterCloudSkills, paginate, savedCloudIds, SKILLS_PAGE_SIZE, VENDOR_NAMES, type SkillSort, type CloudSkill, type Skill, type VendorPick } from "../../lib/skills";
import { getSkillCategory } from "../../lib/skillCategory";
import type { CloudSkillsState } from "../../hooks/useCloudSkills";
import { Modal } from "../settings/Modal";
import { ListFoot } from "./Pager";
import { CategoryGlyph, formatCardDate } from "./SkillCard";
import { AlertIcon, BoxIcon, CheckIcon, ClockIcon, CloudUploadIcon, LoaderIcon, SearchIcon, TrashIcon } from "./icons";

/** lucide `Download`. */
function DownloadIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </svg>
  );
}

/** lucide `User`. */
function UserIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export function OrgSkillsList({
  state,
  refreshing = false,
  pick,
  localSkills,
  versionFilter,
  sort,
  userId,
  mayDelete,
  downloading,
  deleting,
  onDownload,
  onDelete,
  onRetry,
}: {
  state: CloudSkillsState;
  /** A refetch is running over rows already on screen — the year filter is a request now. */
  refreshing?: boolean;
  /** The tree's selection — the list shows this vendor and year only. */
  pick: VendorPick | null;
  /** My Skills — what decides "Saved". */
  localSkills: readonly Skill[];
  /**
   * The scope year and the order, **owned by the pane** (owner).
   *
   * They were this list's own state, with its own pair of controls in its own head row, and
   * that made them a per-tab choice: narrowing to 2024 in My Skills and switching here gave
   * every year again, so the same question had to be answered once per tab. They are a lens on
   * the vendor, not a property of one list, so the pane holds them and every view reads them.
   *
   * The year comes from the instance picked in the instance card now, so the filtering here is a
   * second pass over a response api-v2 has already narrowed — kept because the fetch and the
   * render are not in lockstep: the rows for the previous year are still in hand while the
   * new request is in flight.
   */
  versionFilter: string;
  sort: SkillSort;
  userId: string | null;
  mayDelete: boolean;
  downloading: ReadonlySet<string>;
  deleting: ReadonlySet<string>;
  onDownload: (skill: CloudSkill) => void;
  onDelete: (skill: CloudSkill) => void;
  onRetry: () => void;
}) {
  const { t, locale } = useLocale();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(SKILLS_PAGE_SIZE);
  const [pendingDelete, setPendingDelete] = useState<CloudSkill | null>(null);

  /**
   * Above the early returns below, because hooks cannot be conditional — and back to
   * page 1 whenever the scope changes, since page 3 of another year or another search
   * is disorienting even when it exists. Overshooting a shrunken list is handled by
   * `paginate` clamping instead.
   *
   * The filter and the order are in here too even though they arrive as props now: a page
   * number belongs to a list of a given length, and both of them change that length.
   */
  useEffect(() => {
    setPage(1);
  }, [query, versionFilter, sort, pick?.kind]);
  const saved = useMemo(() => savedCloudIds(localSkills), [localSkills]);

  if (state.kind === "idle" || state.kind === "loading") {
    return (
      <div className="skills-center muted">
        <LoaderIcon size={14} /> {t("common.loading")}
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="settings-card skills-empty">
        <span className="skills-empty-glyph"><AlertIcon size={22} /></span>
        <p className="skills-empty-title">{t("skills.sharedLoadError")}</p>
        <p className="skills-empty-desc">{state.message}</p>
        <button type="button" className="settings-btn secondary" onClick={onRetry}>{t("plugins.retry")}</button>
      </div>
    );
  }
  const inVendor = cloudSkillsForVendor(state.skills, pick);
  /**
   * The year again, client-side, even though api-v2 has now narrowed the request to it.
   *
   * Not redundant. The refetch keeps the previous year's rows on screen while it is in flight, so
   * without this the list would show 2024's skills under a filter that says 2025 for as long as
   * the request takes. It also holds if a row's own metadata disagrees with the server's JSONB
   * filter — which is the same rule the local list applies, so the two tabs cannot answer the
   * same question differently.
   */
  const inVersion = atVersion(inVendor, versionFilter, (s) => cloudSkillTarget(s).version);
  const visible = sortSkills(filterCloudSkills(inVersion, query), sort, {
    version: (s) => cloudSkillTarget(s).version,
    created: (s) => s.created_at,
    name: (s) => s.display_name || s.name,
  });
  // Not `useMemo`: it sits after the early returns above, where a hook cannot go, and
  // it is one `slice`.
  const paged = paginate(visible, page, perPage);
  // The vendor names the scope now; a year would be wrong, since the list spans them.
  const scope = pick ? VENDOR_NAMES[pick.kind] : "";

  return (
    <>
      {/* The search only: the filter and the order are on the pane's title row, shared with
          every other view (owner). */}
      <div className="skills-list-head">
        <div className="search-field">
          <span className="search-field-icon"><SearchIcon size={14} /></span>
          <input className="settings-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("skills.searchPlaceholder")} />
        </div>
      </div>

      {/**
       * The refetch, as a 2px line above the list (owner).
       *
       * Changing the year is a request to api-v2 now, and the rows deliberately stay on screen
       * while it runs — so without this the list simply changes a moment later with nothing to
       * say it was working. A spinner in place of the list would be the blink that keeping the
       * rows was meant to avoid.
       *
       * The track is always rendered and only the fill appears, so starting and stopping moves
       * nothing: reserving the 2px once is cheaper than shifting the whole list twice per year.
       */}
      <div className={`skills-refresh${refreshing ? " is-on" : ""}`} role="status" aria-label={refreshing ? t("common.loading") : undefined}>
        <span />
      </div>

      {/* Same rule as the saved tab: the head row above stays put, the cards below
          scroll in their own region. */}
      <div className="skills-scroll">
      {inVersion.length === 0 ? (
        <div className="skills-dashed">
          {t("skills.noSharedForVersion").replace("{version}", scope)}
        </div>
      ) : visible.length === 0 ? (
        <div className="skills-dashed">{t("skills.noMatch")}</div>
      ) : (
        <div className="skills-grid">
          {paged.items.map((skill) => {
            const name = skill.display_name || skill.name;
            const category = getSkillCategory({ name: skill.name, description: skill.description });
            const isSaved = saved.has(skill.id);
            const isOwner = !!userId && skill.owner_id === userId;
            const busy = downloading.has(skill.id);
            return (
              <div className="skill-card is-cloud" key={skill.id} data-cloud-skill={skill.id}>
                <CategoryGlyph category={category.id} />
                <div className="skill-card-body">
                  <div className="skill-card-title">
                    <span className="skill-card-name">{name}</span>
                    {isSaved ? <span className="skill-badge is-saved"><CheckIcon size={10} /> {t("skills.saved")}</span> : null}
                  </div>
                  {skill.description ? <p className="skill-card-desc">{skill.description}</p> : null}
                  {/* The year is back, and the comment that removed it is why: it said
                      "the tree node on the left already scopes the list to one (owner,
                      2026-08-25)". That stopped being true when the tree became
                      vendor-only — this list spans every year the organization shares, so
                      the card has to say which one, exactly as the saved card does. */}
                  <div className="skill-card-meta">
                    <BoxIcon size={12} />
                    <span>{describeTarget(cloudSkillTarget(skill), t("skills.unknownVersion"))}</span>
                    <span className="sep">·</span>
                    <CloudUploadIcon size={12} />
                    <span>v{skill.published_version}</span>
                    <span className="sep">·</span>
                    <ClockIcon size={12} />
                    <span>{formatCardDate(skill.updated_at || skill.created_at, locale)}</span>
                    {skill.owner_email ? (
                      <>
                        <span className="sep">·</span>
                        <UserIcon />
                        <span className="skill-card-owner">{skill.owner_email}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="skill-card-actions">
                  {mayDelete && isOwner ? (
                    <button
                      type="button"
                      className="icon-btn is-danger"
                      disabled={deleting.has(skill.id)}
                      // One wording for both: on an icon-only control the tooltip *is* the
                      // label, and "Delete" and "Remove from the organization" are not the same
                      // claim — the second is the true one, since local copies survive.
                      aria-label={t("skills.deleteCloud")}
                      title={t("skills.deleteCloud")}
                      onClick={() => setPendingDelete(skill)}
                    >
                      {deleting.has(skill.id) ? <LoaderIcon size={14} /> : <TrashIcon size={14} />}
                    </button>
                  ) : null}
                  {/**
                   * **Icon only, with the label as the tooltip** (owner) — as Delete beside it
                   * already was, and as the saved card's own menu button is.
                   *
                   * It was a labelled `settings-btn`, which made one card in this list carry a
                   * filled control the equivalent card in My Skills does not have: it drew the
                   * eye away from the skill's own name, and at two cards per row a word plus an
                   * icon squeezed the owner's email on the line above. The word survives where
                   * words belong on an icon control — the tooltip and the accessible name.
                   */}
                  {isSaved ? null : (
                    <button
                      type="button"
                      className="icon-btn"
                      disabled={busy}
                      aria-label={t("skills.download")}
                      title={t("skills.download")}
                      onClick={() => onDownload(skill)}
                    >
                      {busy ? <LoaderIcon size={14} /> : <DownloadIcon size={14} />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
      {/* Outside the scroll region, as in the saved tab. */}
      <ListFoot
        page={paged.page}
        pageCount={paged.pageCount}
        total={paged.total}
        perPage={perPage}
        onPerPage={setPerPage}
        onPage={setPage}
      />

      <Modal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title={t("skills.deleteCloudTitle")}
        description={t("skills.deleteCloudDesc")}
        icon={<span className="modal-icon-disc is-danger"><span className="modal-icon-glyph"><AlertIcon size={18} /></span></span>}
        footer={
          <>
            <button type="button" className="settings-btn secondary" onClick={() => setPendingDelete(null)}>{t("common.cancel")}</button>
            <button
              type="button"
              className="settings-btn is-danger"
              onClick={() => {
                if (pendingDelete) onDelete(pendingDelete);
                setPendingDelete(null);
              }}
            >
              {t("toolEditor.delete")}
            </button>
          </>
        }
      >
        <p className="skills-hint">{pendingDelete ? pendingDelete.display_name || pendingDelete.name : ""}</p>
      </Modal>
    </>
  );
}
